import cn from 'classnames';

import SEO from '@components/SEO';
import Layout from '@components/Layout';
import Image from '@components/Image';

import { HeroComponents } from '@blocks';
import NewsletterCallToActionCard from '@components/NewsletterCallToActionCard';
import Link from '@components/Link';

import { getPosts, getSinglePost } from '@util/blog';
import { getClient } from '@sanity-lib/client';

import ArrowRight from '@icons/ArrowRight.svg';

import flowStyles from '@styles/utilities/flow.module.css';
import textStyles from '@styles/textStyles.module.css';
import styles from '@styles/pages/blog.module.css';

import { fetchWithMenu } from '@sanity-lib/util/queries';

import siteInformation from '@content/default.json';
import { blogPageQuery } from '@sanity-lib/queries/pages';
import { isValidSlug } from '@util/slugs';

const { blogPath } = siteInformation;

export async function getStaticPaths() {
  const posts = await getPosts();
  return {
    paths: posts.map(({ slug }) => {
      return {
        params: { slug },
      };
    }),
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params, draftMode = false }) {
  const client = getClient({ preview: draftMode });
  const content = await client.fetch(blogPageQuery);

  if (!isValidSlug(params.slug)) {
    return {
      notFound: true,
    };
  }

  const post = await getSinglePost(params.slug);

  if (post) {
    const { menu } = await fetchWithMenu(client);
    return {
      props: { post, menu, content, draftMode },
    };
  } else {
    return {
      notFound: true,
    };
  }
}

export default function PostTemplate(props) {
  const { post, menu, content } = props;

  const { BlogArticleHero } = HeroComponents;

  const {
    title,
    html,
    feature_image,
    feature_image_alt,
    feature_image_caption,
    excerpt,
    custom_excerpt,
    authors,
    published_at,
    reading_time,
    primary_tag,
  } = post;

  return (
    <Layout heroTheme="light" menu={menu}>
      <SEO
        title={title}
        description={custom_excerpt ? custom_excerpt : excerpt}
        ogImage={feature_image}
      />
      <BlogArticleHero
        heading={title}
        text={custom_excerpt}
        gradient="purple"
        theme="light"
        blogData={{
          primaryTagName: primary_tag?.name,
          primaryTagSlug: primary_tag?.slug,
          publishedAt: published_at,
          readingTime: reading_time,
        }}
      />

      {feature_image && (
        <div className={cn(styles.container, styles.featImage)}>
          <div className={styles.featImageWrapper}>
            <Image
              className={styles.image}
              src={feature_image}
              alt={feature_image_alt}
              fill
              quality={100}
            />
          </div>
          {feature_image_caption && (
            <figcaption
              className={cn(styles.imageCaption, styles.featImageCaption)}
              dangerouslySetInnerHTML={{ __html: feature_image_caption }}
            />
          )}
        </div>
      )}

      {authors && authors.length > 0 && (
        <div className={cn(styles.container, styles.metaAuthor)}>
          {authors.map((author, index) => (
            <div key={index} className={styles.authorImage}>
              {author.profile_image && (
                <Image
                  className={styles.image}
                  src={author.profile_image}
                  alt={`Picture of ${author.name}`}
                  width={54}
                  height={54}
                />
              )}
            </div>
          ))}
          <span className={styles.metaAuthorName}>
            by{' '}
            {authors.map((author, index) => (
              <span key={author.id}>
                {author.name}
                {index < authors.length - 2
                  ? ', '
                  : index === authors.length - 2
                  ? ' and '
                  : ''}
              </span>
            ))}
          </span>
        </div>
      )}

      <main className={styles.main}>
        {custom_excerpt && (
          <div className={cn(styles.container)}>
            <div
              className={cn(styles.customExcerpt, textStyles.headlineM)}
              dangerouslySetInnerHTML={{ __html: custom_excerpt }}
            />
          </div>
        )}

        {html && (
          <div className={cn(styles.container)}>
            <article
              className={cn(styles.article, flowStyles.root)}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>
        )}

        {authors && authors.length > 0 && (
          <div className={cn(styles.container)}>
            <div className={cn(styles.readMoreByAuthor)}>
              {authors.map((author) => (
                <Link
                  key={author.id}
                  href={`${blogPath}/author/${author.slug}`}
                  className={styles.authorLink}
                >
                  <div
                    className={cn(styles.authorImage, styles.authorImageLarge)}
                  >
                    {author.profile_image && (
                      <Image
                        className={styles.image}
                        src={author.profile_image}
                        alt={`Picture of ${author.name}`}
                        width={120}
                        height={120}
                      />
                    )}
                  </div>

                  <div className={styles.authorDetails}>
                    <span className={styles.readMoreBy}>Read more by</span>
                    <span className={styles.authorName}>
                      {author.name} <ArrowRight className={styles.arrow} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <NewsletterCallToActionCard claim={content.ctaClaim} />
      </main>
    </Layout>
  );
}
